import { FC, Fragment } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { selectSortedCasesStructure } from '@core/store/project-slice-selectors';
import classNames from 'classnames';
import { multiplierRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { useAppSelector } from '@core/hooks/redux-hooks';
import { useShouldHighlight } from '../custom-hooks/useShouldHighlight';
import { CaseDto, ForecastDto } from '@app/shared/models/contracts/project-dto';
import { CellValueTheme } from '@app/shared/components/cell-value/cell-value-theme';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import {
  Pwerm2CalculationResultsDto,
  Pwerm2CalculationWaterfallCaseYearMultipleInstrumentDto,
  Pwerm2CalculationWaterfallCaseYearMultipleInstrumentTrancheDto,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import {
  getWaterfallCaseYearMultipleInstrumentTrancheValue,
  getWaterfallCaseYearMultipleInstrumentValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import SvgInformation from '@app/shared/icons/Information';
import { useLocale } from '@app/core/hooks/useLocale';
import { usePwcNavigate } from '@app/core/hooks/routing-hooks';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import styles from './table-section-values.module.scss';
import { SponsorIrrAndHurdleTab } from '@app/shared/models/contracts/enums/shared-enums';
import { buildFocusModeSearchParams } from '@app/shared/hooks/focus-mode';
import { FocusModeLocationState } from '@app/shared/hooks/focus-mode/focus-mode-types';
import { useLocation } from 'react-router-dom';

export enum TooltipSection {
  Instrument = 'Instrument',
  Tranche = 'Tranche',
}

export enum SymbolType {
  TopLine = 'TopLine',
  PlusSign = 'PlusSign',
}

type SymbolTooltipInfo = {
  instrumentId: string;
  trancheId?: string;
  results: Pwerm2CalculationResultsDto;
  symbolType?: SymbolType;
};

type TableSectionValuesPerMultipleProps = {
  title: string;
  renderCell: (props: {
    caseItem: CaseDto;
    caseItemIndex: number;
    forecast: ForecastDto;
    multiple: number; // todo replace usage of this with multipleId
    multipleId?: string;
  }) => JSX.Element;
  computedCellClassNames?: (props: {
    caseItem: CaseDto;
    caseItemIndex: number;
    forecast: ForecastDto;
    multiple: number;
    multipleId?: string;
  }) => string;
  subTitle?: string;
  renderSpacer?: boolean;
  testId?: string;
  symbolTooltipInfo?: SymbolTooltipInfo;
  sponsorIrrTooltipInfo?: string;
};

const TableSectionValuesPerMultiple: FC<TableSectionValuesPerMultipleProps> = ({
  title,
  renderCell,
  computedCellClassNames,
  subTitle: subHeader,
  renderSpacer = true,
  testId,
  symbolTooltipInfo,
  sponsorIrrTooltipInfo,
}) => {
  const { l } = useLocale();
  const casesStructure = useAppSelector(selectSortedCasesStructure);
  const shouldHighlightCell = useShouldHighlight();
  const maxMultiples = Math.max(...casesStructure.map((x) => x.multiples.length));
  const { generatePath, navigate } = usePwcNavigate();
  const location = useLocation();

  const checkWaterfallCondition = (
    symbolTooltipInfo: SymbolTooltipInfo | undefined,
    casesStructure: CaseDto[],
    predicate: (
      inst:
        | Pwerm2CalculationWaterfallCaseYearMultipleInstrumentDto
        | Pwerm2CalculationWaterfallCaseYearMultipleInstrumentTrancheDto
    ) => boolean
  ): boolean => {
    if (!symbolTooltipInfo) {
      return false;
    }

    return casesStructure.some((caseItem) =>
      caseItem.forecasts.some((forecast) =>
        caseItem.multiples.some((multiple) =>
          symbolTooltipInfo.trancheId
            ? Boolean(
                getWaterfallCaseYearMultipleInstrumentTrancheValue(
                  symbolTooltipInfo.results,
                  caseItem.caseId,
                  forecast.forecastId,
                  multiple.multipleId!,
                  symbolTooltipInfo.instrumentId,
                  symbolTooltipInfo.trancheId,
                  predicate
                )
              )
            : Boolean(
                getWaterfallCaseYearMultipleInstrumentValue(
                  symbolTooltipInfo.results,
                  caseItem.caseId,
                  forecast.forecastId,
                  multiple.multipleId!,
                  symbolTooltipInfo.instrumentId,
                  predicate
                )
              )
        )
      )
    );
  };

  const hasAnyPlusSign = checkWaterfallCondition(
    symbolTooltipInfo,
    casesStructure,
    (inst) => inst.hasReceivedEnhancedParticipation
  );

  const hasHitCap = checkWaterfallCondition(
    symbolTooltipInfo,
    casesStructure,
    (inst) => inst.hasHitCap
  );

  const shouldTooltipBeDisplayed =
    (symbolTooltipInfo && hasAnyPlusSign && symbolTooltipInfo.symbolType === SymbolType.PlusSign) ||
    (symbolTooltipInfo && hasHitCap && symbolTooltipInfo.symbolType === SymbolType.TopLine);

  const tooltipContent =
    symbolTooltipInfo && symbolTooltipInfo.symbolType === SymbolType.PlusSign
      ? l('_HasReceivedEnhancedParticipationTooltip')
      : l('_HasHitCapTooltip');

  const handleClick = (caseId: string, forecastId: string, multipleId: string) => {
    const exitDate = casesStructure
      .find((caseItem) => caseItem.caseId === caseId)
      ?.forecasts.find((forecast) => forecast.forecastId === forecastId)?.forecastYear;

    if (!exitDate) {
      return;
    }

    const selections = {
      type: SponsorIrrAndHurdleTab.SponsorIRR as const,
      case: caseId,
      year: exitDate,
      multiple: multipleId,
    };
    const params = buildFocusModeSearchParams(selections);
    const newPath = generatePath(
      `../${RouteConstants.SponsorIRRAndHurdle}/${RouteConstants.SponsorIRR}/${caseId}`
    );
    navigate(`${newPath}?${params}`, {
      state: { returnPath: location.pathname } as FocusModeLocationState,
    });
  };

  const renderTooltipButton = (content: string) => (
    <Tooltip content={content} placement={TooltipPlacement.Right} useFixedPositioning>
      <Button appearance={ButtonAppearance.CLEAN} size={ButtonSize.FLUID} aria-label={content}>
        <SvgInformation className={styles['warning-icon']} />
      </Button>
    </Tooltip>
  );

  return (
    <>
      {Array.from(Array(maxMultiples).keys()).map((multipleIndex) => (
        <tr
          key={multipleIndex}
          data-testid={multipleIndex === 0 && testId ? `${title}-${testId}` : undefined}>
          <th className={classNames('table-primary__cell--vertical-separator')}>
            {multipleIndex === 0 && (shouldTooltipBeDisplayed || sponsorIrrTooltipInfo) ? (
              <div className={styles['tooltip-header']}>
                {title}
                {shouldTooltipBeDisplayed && renderTooltipButton(tooltipContent)}
                {sponsorIrrTooltipInfo && renderTooltipButton(sponsorIrrTooltipInfo)}
              </div>
            ) : multipleIndex === 0 ? (
              title
            ) : multipleIndex === 1 ? (
              subHeader
            ) : (
              ''
            )}
          </th>
          {casesStructure.map((caseItem, caseItemIndex) => {
            const multiple = caseItem.multiples[multipleIndex];
            const multipleId = multiple?.multipleId;
            return (
              <Fragment key={caseItemIndex}>
                <td className="table-primary__cell--vertical-separator">
                  {multiple && (
                    <CellValue
                      {...multiplierRules}
                      value={multiple.multiple}
                      theme={CellValueTheme.Tertiary}
                    />
                  )}
                </td>
                {caseItem.forecasts.map((forecast, forecastIndex) => (
                  <td
                    key={forecastIndex}
                    data-testid={
                      testId
                        ? `${testId}-c${caseItemIndex}-f${forecastIndex}-m${multipleIndex}`
                        : undefined
                    }
                    className={classNames(
                      multiple?.multiple &&
                        computedCellClassNames?.({
                          caseItem,
                          caseItemIndex,
                          forecast: forecast,
                          multiple: multiple.multiple,
                          multipleId,
                        }),
                      {
                        'table-primary__cell--vertical-separator':
                          caseItem.forecasts.length - 1 === forecastIndex,
                        'table-primary__cell--highlight':
                          multiple?.multiple &&
                          shouldHighlightCell(
                            caseItem.caseId,
                            forecast.forecastId,
                            multiple?.multiple
                          ),
                        [styles['clickable-cell']]: sponsorIrrTooltipInfo,
                      }
                    )}
                    onClick={
                      sponsorIrrTooltipInfo
                        ? () => {
                            handleClick(caseItem.caseId, forecast.forecastId, multipleId);
                          }
                        : undefined
                    }>
                    {multiple?.multiple &&
                      renderCell({
                        caseItem,
                        caseItemIndex,
                        forecast: forecast,
                        multiple: multiple.multiple,
                        multipleId,
                      })}
                  </td>
                ))}
              </Fragment>
            );
          })}
        </tr>
      ))}
      {renderSpacer && <TableSpacerRow />}
    </>
  );
};

export default TableSectionValuesPerMultiple;
